import first from "lodash/first";
import last from "lodash/last";
import { getDayCount } from "app/utils/utils";
import { get, isEmpty, slice } from "lodash";
import { FLIGHT_TYPE, ITINERARY_INFO_TYPE } from "app/constants";
import StopoverFlightDurationLabel from "app/pages/Stopover/StopoverFlightDurationLabel/StopoverFlightDurationLabel";
import LayoverCountDisplay from "app/pages/.shared/ItineraryUtils/LayoverCountDisplay";

const generateItineraryInfo = ({
	itineraryType,
	flightType,
	flightData,
	isStopover,
	arrivalDaysCounter,
	isNextDay,
}) => {
	return {
		type: itineraryType,
		flightType,
		data: {
			airport: flightData.airport,
			iata: flightData.iata,
			iataCity: flightData.iataCity,
			date:
				flightType === FLIGHT_TYPE.DEPARTURE
					? flightData.takeOffDate
					: flightData.landingDate,
			time:
				flightType === FLIGHT_TYPE.DEPARTURE
					? flightData.takeOffTime
					: flightData.landingTime,
			...(typeof isNextDay === "boolean" && { isNextDay }),
			...(isStopover && { isFlightStopOver: true }),
			...(flightType === FLIGHT_TYPE.ARRIVAL &&
				typeof arrivalDaysCounter === "number" && { arrivalDaysCounter }),
		},
	};
};

const generateFlightInfo = ({
	itineraryType,
	flightData,
	stopCount,
	legInfo,
	isFlightLeg,
	isStopover,
	isRecap,
}) => {
	if (isRecap) {
		return {
			type: ITINERARY_INFO_TYPE.FLIGHT_INFO,
			data: {
				duration: flightData.totalDuration,
				stopCount,
				legInfo,
				airlineCode: flightData.airline.code,
				...(isStopover && { isFlightStopOver: true }),
			},
		};
	}

	if (isFlightLeg) {
		return {
			type: ITINERARY_INFO_TYPE.FLIGHT_INFO,
			data: {
				duration: flightData.to.flightDuration,
				airlineCode: flightData.airline.code,
				airlineName: flightData.airline.name,
				flightNumber: flightData.flightNumber,
				isDirect: stopCount === 0,
				...(stopCount === 0 && { legInfo: legInfo }),
				...(flightData.operatingAirlineName && {
					operatingAirlineName: flightData.operatingAirlineName,
				}),
			},
		};
	}

	if (isStopover) {
		return {
			type: ITINERARY_INFO_TYPE.STOPOVER_PREVIEW_INFO,
			data: {
				duration: flightData?.stopOverDuration,
				airlineCode: flightData.airline.code,
				stopOverCity: flightData?.stopOverCity,
				...(stopCount - 1 > 0 && {
					stopCount: <LayoverCountDisplay count={stopCount - 1} />,
				}), // - stopover
			},
		};
	}

	return {
		...(itineraryType && { type: itineraryType }),
		data: {
			...(flightData?.totalDuration && { duration: flightData?.totalDuration }),
			...(typeof stopCount === "number" && { stopCount, isDirect: stopCount === 0 }),
			...(legInfo && { legInfo }),
			...(flightData?.airline?.code && { airlineCode: flightData.airline.code }),
		},
	};
};

export const buildItineraryPreviewData = (flight, isRecap = false) => {
	if (isEmpty(flight) || get(flight, "legs", []).length === 0) {
		return [];
	}

	const firstLeg = first(flight.legs);
	const lastLeg = last(flight.legs);
	const stopCount = flight.legs.length - 1;
	const legInfo = <LayoverCountDisplay count={stopCount} />;
	const isStopover = flight.legs?.some(leg => leg?.from?.isStopOver || leg?.to?.isStopOver);
	const arrivalDaysCounter = getDayCount(firstLeg.from.takeOffDate, lastLeg.to.landingDate) || 0;

	const departureInfo = generateItineraryInfo({
		itineraryType: ITINERARY_INFO_TYPE.SCHEDULE,
		flightType: FLIGHT_TYPE.DEPARTURE,
		flightData: firstLeg.from,
		isStopover,
	});

	const arrivalInfo = generateItineraryInfo({
		itineraryType: ITINERARY_INFO_TYPE.SCHEDULE,
		flightType: FLIGHT_TYPE.ARRIVAL,
		flightData: lastLeg.to,
		isStopover,
		arrivalDaysCounter,
	});

	const flightInfo = generateFlightInfo({
		itineraryType: ITINERARY_INFO_TYPE.FLIGHT_PREVIEW_INFO,
		flightData: flight,
		stopCount,
		legInfo,
		isStopover,
		isRecap,
	});

	return [departureInfo, flightInfo, arrivalInfo];
};

export const buildItineraryDetailData = flight => {
	if (isEmpty(flight) || get(flight, "legs", []).length === 0) {
		return [];
	}

	const stopCount = flight.legs.length - 1;
	const firstLeg = first(flight.legs);

	const timelineData = [];

	flight.legs.forEach((leg, index) => {
		const isArrivalNextDay =
			(getDayCount(firstLeg.from.takeOffDate, leg.to.landingDate) || 0) > 0;
		const isDepartNextDay =
			(getDayCount(firstLeg.from.takeOffDate, leg.from.takeOffDate) || 0) > 0;
		const legInfo = <LayoverCountDisplay count={0} />;

		const departEvent = generateItineraryInfo({
			itineraryType: ITINERARY_INFO_TYPE.SCHEDULE,
			flightType: FLIGHT_TYPE.DEPARTURE,
			flightData: leg.from,
			isStopover: leg?.from?.isStopOver,
			isNextDay: isDepartNextDay,
		});

		const arrivalEvent = generateItineraryInfo({
			itineraryType: ITINERARY_INFO_TYPE.SCHEDULE,
			flightType: FLIGHT_TYPE.ARRIVAL,
			flightData: leg.to,
			isStopover: leg?.to?.isStopOver,
			isNextDay: isArrivalNextDay,
		});

		const flightInfo = generateFlightInfo({
			itineraryType: ITINERARY_INFO_TYPE.FLIGHT_INFO,
			flightData: leg,
			stopCount,
			legInfo,
			isFlightLeg: true,
		});

		timelineData.push(departEvent);
		timelineData.push(flightInfo);
		timelineData.push(arrivalEvent);

		const hasTransitStop = index < flight.legs.length - 1 && leg.to?.stopDuration;
		if (hasTransitStop) {
			const transitInfo = {
				type: leg.to?.isStopOver
					? ITINERARY_INFO_TYPE.STOPOVER_INFO
					: ITINERARY_INFO_TYPE.TRANSIT_INFO,
				data: {
					duration: leg.to.stopDuration,
				},
			};

			timelineData.push(transitInfo);
		}
	});

	return timelineData;
};

export const convertDurationLabel = duration => {
	if (duration && duration.length > 0) {
		const durationItems = duration.split(":");
		if (durationItems.length === 2) {
			return `${durationItems[0]}h ${durationItems[1]}m`;
		}
		if (durationItems.length === 3) {
			return `${durationItems[0]}d ${durationItems[1]}h ${durationItems[2]}m`;
		}
	}
	return "";
};

export const buildStopOverItineraryPreviewData = flight => {
	if (isEmpty(flight) || get(flight, "legs", []).length === 0) {
		return [];
	}

	const firstLeg = first(flight.legs);
	const lastLeg = last(flight.legs);

	const indexStopOver = flight.legs.findIndex(leg => leg?.to?.isStopOver === true);
	if (indexStopOver === -1) {
		return buildItineraryPreviewData(flight, true);
	}

	const departStopOverLeg = flight.legs[indexStopOver];
	const arrivalStopOverLeg = flight.legs[indexStopOver + 1] || null;

	const departureLegs = slice(flight.legs, 0, indexStopOver + 1); // all the legs from departure to stopover
	const arrivalLegs = slice(flight.legs, indexStopOver + 1); // all the legs from stopover to arrival

	const arrivalStopCount = arrivalLegs?.length - 1 || 0;
	const departureStopCount = departureLegs?.length - 1 || 0;

	const stopOverDayCount = flight?.stopOverDuration;
	const stopOverCountry = flight?.stopOverCity;

	const departureLegInfo = <LayoverCountDisplay count={departureStopCount} />;
	const arrivalLegInfo = <LayoverCountDisplay count={arrivalStopCount} />;
	const stopOverLegInfo = stopOverDayCount && (
		<StopoverFlightDurationLabel
			stopOverDayCount={stopOverDayCount}
			stopOverCountry={stopOverCountry}
			className="stop-duration"
			shorten
		/>
	);

	const departEvent = generateItineraryInfo({
		itineraryType: ITINERARY_INFO_TYPE.SCHEDULE,
		flightType: FLIGHT_TYPE.DEPARTURE,
		flightData: firstLeg.from,
	});

	const departStopOverEvent = generateItineraryInfo({
		itineraryType: ITINERARY_INFO_TYPE.STOPOVER_SCHEDULE,
		flightType: FLIGHT_TYPE.ARRIVAL,
		flightData: departStopOverLeg?.to,
	});

	const arrivalStopOverEvent = generateItineraryInfo({
		itineraryType: ITINERARY_INFO_TYPE.STOPOVER_SCHEDULE,
		flightType: FLIGHT_TYPE.DEPARTURE,
		flightData: arrivalStopOverLeg?.from,
	});

	const arrivalEvent = generateItineraryInfo({
		itineraryType: ITINERARY_INFO_TYPE.SCHEDULE,
		flightType: FLIGHT_TYPE.ARRIVAL,
		flightData: lastLeg.to,
	});

	const departureFlightInfo = generateFlightInfo({
		itineraryType: ITINERARY_INFO_TYPE.FLIGHT_INFO,
		stopCount: departureStopCount,
		legInfo: departureLegInfo,
	});

	const stopOverInfo = generateFlightInfo({
		itineraryType: ITINERARY_INFO_TYPE.STOPOVER_INFO,
		legInfo: stopOverLegInfo,
	});

	const arrivalFlightInfo = generateFlightInfo({
		itineraryType: ITINERARY_INFO_TYPE.FLIGHT_INFO,
		stopCount: arrivalStopCount,
		legInfo: arrivalLegInfo,
	});

	return [
		departEvent,
		departureFlightInfo,
		departStopOverEvent,
		stopOverInfo,
		arrivalStopOverEvent,
		arrivalFlightInfo,
		arrivalEvent,
	];
};
